export const DEFAULT_RESEARCH_SESSION_TITLE = 'New research session';
const FALLBACK_RESEARCH_SESSION_TITLE = 'Research session';
const TITLE_MAX_CHARS = 72;
const GENERIC_SESSION_TITLES = new Set([
  DEFAULT_RESEARCH_SESSION_TITLE.toLowerCase(),
  FALLBACK_RESEARCH_SESSION_TITLE.toLowerCase(),
  'research query',
  'new chat',
  'new task',
]);

type ResearchSessionTitleLike = {
  name?: string | null;
  promptText?: string | null;
  latestMessagePreview?: string | null;
};

function normalizeTitleText(value: string | null | undefined): string {
  return String(value || '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_#>]+/g, '')
    .replace(/^title:\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^["'“”‘’]+|["'“”‘’]+$/g, '')
    .trim();
}

function truncateTitle(value: string): string {
  if (value.length <= TITLE_MAX_CHARS) {
    return value;
  }
  const sliced = value.slice(0, TITLE_MAX_CHARS - 1);
  const wordBoundary = sliced.lastIndexOf(' ');
  return `${(wordBoundary > 32 ? sliced.slice(0, wordBoundary) : sliced).trim()}...`;
}

export function isGenericResearchSessionTitle(value: string | null | undefined): boolean {
  const normalized = normalizeTitleText(value).toLowerCase();
  return !normalized || GENERIC_SESSION_TITLES.has(normalized);
}

export function sanitizeGeneratedSessionTitle(value: string | null | undefined): string | null {
  const normalized = normalizeTitleText(value)
    .split('\n')[0]
    .replace(/[.!?]+$/g, '')
    .trim();
  if (isGenericResearchSessionTitle(normalized)) {
    return null;
  }
  return truncateTitle(normalized);
}

export function buildFallbackSessionTitleFromUserMessage(
  message: string | null | undefined,
  fallback = FALLBACK_RESEARCH_SESSION_TITLE
): string {
  const normalized = normalizeTitleText(message);
  if (
    !normalized ||
    normalized === '(See attached image)' ||
    normalized === '(See attached document)'
  ) {
    return fallback;
  }
  return truncateTitle(normalized);
}

export function buildSessionTitlePrompt(params: {
  firstUserMessage: string;
  brandName?: string | null;
}): string {
  return [
    'Create a concise chat title from the first user message.',
    'Rules: 3-6 words, specific noun phrase, no quotes, no punctuation at the end.',
    params.brandName ? `Workspace brand: ${params.brandName}` : null,
    `First user message: ${params.firstUserMessage}`,
  ]
    .filter(Boolean)
    .join('\n');
}

export function getResearchSessionDisplayTitle(session: ResearchSessionTitleLike): string {
  const explicitName = normalizeTitleText(session.name);
  if (explicitName) {
    return truncateTitle(explicitName);
  }

  const promptTitle = sanitizeGeneratedSessionTitle(session.promptText);
  if (promptTitle) {
    return promptTitle;
  }

  const previewTitle = sanitizeGeneratedSessionTitle(session.latestMessagePreview);
  if (previewTitle) {
    return previewTitle;
  }

  return normalizeTitleText(session.promptText) || DEFAULT_RESEARCH_SESSION_TITLE;
}
